/**
 * Dispatch, recall and lot balance (F7.8 · F7.9 · F7-B1.b) rows as the console keeps them: weights
 * in integer grams, snake_case raw rows mapped once here. Pure so the tests can feed real samples.
 */
import { arr, bool, iso, kgToGrams, kgToGrams0, num, numOrNull, obj, str, strOrNull } from '../../api/mappers'

// ------------------------------ dispatches ------------------------------

export interface DispatchItem {
  itemId: string
  packLotId: string
  packLotCode: string
  skuName: string | null
  /** Pouch net weight. */
  netG: number | null
  pouches: number
  loadedAt: string | null
}

export interface DispatchRow {
  id: string
  code: string
  status: string
  toOrgId: string | null
  toOrg: string | null
  challanNo: string | null
  vehicleNo: string | null
  driverName: string | null
  driverPhone: string | null
  vehicleClean: boolean
  dispatchedAt: string | null
  fromLocationId: string | null
  fromLocation: string | null
  items: DispatchItem[]
  /** Σ pouches over the items. */
  pouches: number
  /** Distinct pack lots on the trip. */
  packLots: number
}

export const mapDispatchItem = (r: Record<string, unknown>): DispatchItem => ({
  itemId: str(r.item_id),
  packLotId: str(r.pack_lot_id),
  packLotCode: str(r.code),
  skuName: strOrNull(r.sku_name),
  netG: numOrNull(r.net_g),
  pouches: num(r.pouches),
  loadedAt: iso(r.loaded_at),
})

export const mapDispatch = (r: Record<string, unknown>): DispatchRow => {
  const items = arr(r.items).map((i) => mapDispatchItem(obj(i)))
  return {
    id: str(r.id),
    code: str(r.code),
    status: str(r.status),
    toOrgId: strOrNull(r.to_org_id),
    toOrg: strOrNull(r.to_org),
    challanNo: strOrNull(r.challan_no),
    vehicleNo: strOrNull(r.vehicle_no),
    driverName: strOrNull(r.driver_name),
    driverPhone: strOrNull(r.driver_phone),
    vehicleClean: bool(r.vehicle_clean),
    dispatchedAt: iso(r.dispatched_at),
    fromLocationId: strOrNull(r.from_location_id),
    fromLocation: strOrNull(r.from_location),
    items,
    pouches: items.reduce((s, i) => s + i.pouches, 0),
    packLots: new Set(items.map((i) => i.packLotId)).size,
  }
}

/** A pack lot that may go on a trip (GET /pack-lots?status=RELEASED&hold=NONE). */
export interface PackLotOption {
  id: string
  code: string
  skuName: string | null
  status: string
  hold: string
  pouchCount: number
  pouchesDispatched: number
  pouchesLeft: number
}
export const mapPackLotOption = (r: Record<string, unknown>): PackLotOption => {
  const pouchCount = num(r.pouch_count)
  const dispatched = num(r.pouches_dispatched)
  return {
    id: str(r.id),
    code: str(r.code),
    skuName: strOrNull(r.sku_name),
    status: str(r.status),
    hold: str(r.hold, 'NONE'),
    pouchCount,
    pouchesDispatched: dispatched,
    pouchesLeft: r.pouches_left !== undefined ? num(r.pouches_left) : pouchCount - dispatched,
  }
}

/** A recalled pack lot (GET /pack-lots?hold=RECALLED). */
export interface RecalledPackLot extends PackLotOption {
  batchCode: string | null
  batchUnitId: string | null
  packDate: string | null
  updatedAt: string | null
}
export const mapRecalledPackLot = (r: Record<string, unknown>): RecalledPackLot => ({
  ...mapPackLotOption(r),
  batchCode: strOrNull(r.batch_code),
  batchUnitId: strOrNull(r.batch_unit_id),
  packDate: strOrNull(r.pack_date),
  updatedAt: iso(r.updated_at),
})

// ------------------------------ lot balance ------------------------------

export interface LotBalanceRow {
  lotId: string
  lotCode: string
  farmerId: string | null
  cropId: string | null
  seasonCode: string | null
  stream: string | null
  status: string
  hold: string
  gateNetG: number
  receivedG: number
  issuedG: number
  inLotBagsG: number
  brandBatchG: number
  convBatchG: number
  byproductG: number
  wasteG: number
  packedWaitingG: number
  packedReleasedG: number
  packedHeldG: number
  packedDestroyedG: number
  brandPackedG: number
  openRuns: number
  accountedG: number
  /** null when the lot has no gate weight. */
  accountedPct: number | null
  lostG: number
  overPacked: boolean
  flagged: boolean
  flagReasons: string[]
  /** The line below which a lot is flagged (98 by default). */
  accountedMinPct: number
}

export const mapLotBalance = (r: Record<string, unknown>): LotBalanceRow => ({
  lotId: str(r.lot_id),
  lotCode: str(r.lot_code),
  farmerId: strOrNull(r.farmer_id),
  cropId: strOrNull(r.crop_id),
  seasonCode: strOrNull(r.season_code),
  stream: strOrNull(r.stream),
  status: str(r.status),
  hold: str(r.hold, 'NONE'),
  gateNetG: kgToGrams0(r.gate_net_kg as string),
  receivedG: kgToGrams0(r.received_kg as string),
  issuedG: kgToGrams0(r.issued_kg as string),
  inLotBagsG: kgToGrams0(r.in_lot_bags_kg as string),
  brandBatchG: kgToGrams0(r.brand_batch_kg as string),
  convBatchG: kgToGrams0(r.conv_batch_kg as string),
  byproductG: kgToGrams0(r.byproduct_kg as string),
  wasteG: kgToGrams0(r.waste_kg as string),
  packedWaitingG: kgToGrams0(r.packed_waiting_kg as string),
  packedReleasedG: kgToGrams0(r.packed_released_kg as string),
  packedHeldG: kgToGrams0(r.packed_held_kg as string),
  packedDestroyedG: kgToGrams0(r.packed_destroyed_kg as string),
  brandPackedG: kgToGrams0(r.brand_packed_kg as string),
  openRuns: num(r.open_runs),
  accountedG: kgToGrams0(r.accounted_kg as string),
  accountedPct: numOrNull(r.accounted_pct),
  lostG: kgToGrams0(r.lost_kg as string),
  overPacked: bool(r.over_packed),
  flagged: bool(r.flagged),
  flagReasons: arr<unknown>(r.flag_reasons).map(String),
  accountedMinPct: num(r.accounted_min_pct, 98),
})

export interface LotBalanceSeason {
  seasonCode: string
  cropId: string
  cropName: string
  lots: number
  gateNetG: number
  issuedG: number
  brandBatchG: number
  convBatchG: number
  byproductG: number
  wasteG: number
  packedReleasedG: number
  packedHeldG: number
  packedDestroyedG: number
  accountedG: number
  accountedPct: number | null
  lotsOverPacked: number
}
export const mapLotBalanceSeason = (r: Record<string, unknown>): LotBalanceSeason => ({
  seasonCode: str(r.season_code),
  cropId: str(r.crop_id),
  cropName: str(r.crop_name),
  lots: num(r.lots),
  gateNetG: kgToGrams0(r.gate_net_kg as string),
  issuedG: kgToGrams0(r.issued_kg as string),
  brandBatchG: kgToGrams0(r.brand_batch_kg as string),
  convBatchG: kgToGrams0(r.conv_batch_kg as string),
  byproductG: kgToGrams0(r.byproduct_kg as string),
  wasteG: kgToGrams0(r.waste_kg as string),
  packedReleasedG: kgToGrams0(r.packed_released_kg as string),
  packedHeldG: kgToGrams0(r.packed_held_kg as string),
  packedDestroyedG: kgToGrams0(r.packed_destroyed_kg as string),
  accountedG: kgToGrams0(r.accounted_kg as string),
  accountedPct: numOrNull(r.accounted_pct),
  lotsOverPacked: num(r.lots_over_packed),
})

/**
 * The seasons summary of GET /lot-balance. The service returns it as `seasons` next to the page;
 * the envelope only passes `summary` through, so accept `summary` as the array or `{ seasons }`.
 */
export function seasonsFrom(summary: unknown): LotBalanceSeason[] {
  const list = Array.isArray(summary) ? summary : arr(obj(summary).seasons)
  return list.map((s) => mapLotBalanceSeason(obj(s)))
}

// ------------------------------ recall export ------------------------------

export interface RecallExport {
  code: string
  start: { code: string; unitType: string; packLotId: string | null }
  farmers: Array<{ farmerId: string; name: string | null; farmerCode: string | null }>
  lots: Array<{
    unitId: string
    code: string
    farmerId: string | null
    seasonCode: string | null
    netG: number | null
    hold: string
    status: string
  }>
  runs: Array<{
    id: string
    code: string
    stepCode: string
    status: string
    startedAt: string | null
    endedAt: string | null
    notClosed: boolean
  }>
  batches: Array<{ unitId: string; code: string; stream: string; status: string; hold: string; netG: number | null }>
  otherOutputs: Array<{ unitId: string; code: string; unitType: string; stream: string; netG: number | null }>
  packLots: Array<{
    id: string
    code: string
    pouches: number | null
    status: string
    hold: string
    pouchesDispatched: number
  }>
  customers: Array<{
    orgId: string
    org: string
    packLotCode: string
    challanNo: string | null
    pouches: number
    dispatchedAt: string | null
    shipmentCode: string | null
  }>
  balance: LotBalanceRow[]
  elapsedMs: number
  generatedAt: string | null
  generatedBy: { id: string | null; name: string | null; username: string | null }
}

export const mapRecallExport = (r: Record<string, unknown>): RecallExport => {
  const start = obj(r.start)
  const by = obj(r.generated_by)
  return {
    code: str(r.code),
    start: {
      code: str(start.code, str(r.code)),
      unitType: str(start.unit_type),
      packLotId: strOrNull(start.pack_lot_id),
    },
    farmers: arr(r.farmers).map((f) => ({
      farmerId: str(f.farmer_id),
      name: strOrNull(f.name),
      farmerCode: strOrNull(f.farmer_code),
    })),
    lots: arr(r.lots).map((l) => ({
      unitId: str(l.unit_id),
      code: str(l.code),
      farmerId: strOrNull(l.farmer_id),
      seasonCode: strOrNull(l.season_code),
      netG: kgToGrams(l.net_kg as string),
      hold: str(l.hold, 'NONE'),
      status: str(l.status),
    })),
    runs: arr(r.runs).map((x) => ({
      id: str(x.id),
      code: str(x.code),
      stepCode: str(x.step_code),
      status: str(x.status),
      startedAt: iso(x.started_at),
      endedAt: iso(x.ended_at),
      notClosed: bool(x.not_closed),
    })),
    batches: arr(r.batches).map((b) => ({
      unitId: str(b.unit_id),
      code: str(b.code),
      stream: str(b.stream),
      status: str(b.status),
      hold: str(b.hold, 'NONE'),
      netG: kgToGrams(b.net_kg as string),
    })),
    otherOutputs: arr(r.other_outputs).map((o) => ({
      unitId: str(o.unit_id),
      code: str(o.code),
      unitType: str(o.unit_type),
      stream: str(o.stream),
      netG: kgToGrams(o.net_kg as string),
    })),
    packLots: arr(r.pack_lots).map((p) => ({
      id: str(p.id),
      code: str(p.code),
      pouches: numOrNull(p.pouches),
      status: str(p.status),
      hold: str(p.hold, 'NONE'),
      pouchesDispatched: num(p.pouches_dispatched),
    })),
    customers: arr(r.customers).map((c) => ({
      orgId: str(c.org_id),
      org: str(c.org),
      packLotCode: str(c.pack_lot_code),
      challanNo: strOrNull(c.challan_no),
      pouches: num(c.pouches),
      dispatchedAt: iso(c.dispatched_at),
      shipmentCode: strOrNull(c.shipment_code),
    })),
    balance: arr(r.balance).map(mapLotBalance),
    elapsedMs: num(r.elapsed_ms),
    generatedAt: iso(r.generated_at),
    generatedBy: { id: strOrNull(by.id), name: strOrNull(by.name), username: strOrNull(by.username) },
  }
}

/** Pouches of the found pack lots still out with customers or at the hall (what a recall would hold). */
export function recallReach(e: RecallExport): { packLots: number; pouches: number; pouchesOut: number; customers: number } {
  return {
    packLots: e.packLots.length,
    pouches: e.packLots.reduce((s, p) => s + (p.pouches ?? 0), 0),
    pouchesOut: e.packLots.reduce((s, p) => s + p.pouchesDispatched, 0),
    customers: new Set(e.customers.map((c) => c.orgId)).size,
  }
}

// ------------------------------ trace back samples ------------------------------

export interface TraceBackSamples {
  unitId: string
  unitCode: string
  farmersCount: number
  oneFarmerExceptionId: string | null
  lots: Array<{
    unitId: string
    code: string
    farmerId: string | null
    seasonCode: string | null
    hold: string
    sample: {
      id: string
      seal: string | null
      status: string
      retainUntil: string | null
      locationCode: string | null
      locationPath: string | null
    } | null
    sampleUsable: boolean
    exceptionId: string | null
  }>
}
export const mapTraceBackSamples = (r: Record<string, unknown>): TraceBackSamples => ({
  unitId: str(r.unit_id),
  unitCode: str(r.unit_code),
  farmersCount: num(r.farmers_count),
  oneFarmerExceptionId: strOrNull(r.one_farmer_exception_id),
  lots: arr(r.lots).map((l) => {
    const s = l.retained_sample ? obj(l.retained_sample) : null
    return {
      unitId: str(l.unit_id),
      code: str(l.code),
      farmerId: strOrNull(l.farmer_id),
      seasonCode: strOrNull(l.season_code),
      hold: str(l.hold, 'NONE'),
      sample: s
        ? {
            id: str(s.id),
            seal: strOrNull(s.seal),
            status: str(s.status),
            retainUntil: strOrNull(s.retain_until),
            locationCode: strOrNull(s.location_code),
            locationPath: strOrNull(s.location_path),
          }
        : null,
      sampleUsable: bool(l.sample_usable),
      exceptionId: strOrNull(l.exception_id),
    }
  }),
})
